import { Box, Divider, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { formatActivityType } from '@shinetools/ape-code-library';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import { VerificationModuleData } from '../../machine/model';
import ResetOnboardingApplicationModal from '../ResetOnboardingApplicationModal';
import CompanyInformation from './components/CompanyInformation';
import FiscalInformation from './components/FiscalInformation';
import LegalInformation from './components/LegalInformation';
import locales from './locales';

type GeneralInformationVerificationProps = {
  moduleData: VerificationModuleData;
  onAbort: () => void;
};

export const GeneralInformationVerification =
  asBentoPage<GeneralInformationVerificationProps>(
    ({ Layout, moduleData, onAbort, onPrev }) => {
      const modalProps = useDisclosure({ defaultIsOpen: false });

      const { company, profile, taxResidencies } =
        moduleData.verificationData.viewer;

      const { incomeRange, isSpecifiedUSPerson, personalAssets } = profile;

      const {
        activityType,
        city,
        country,
        legalForm,
        legalName,
        legalRegistrationNumber,
        registrationDate,
        street,
        streetAddition,
        tradeName,
        zip,
      } = company.profile;

      const hasMultipleBeneficiaries =
        (company.legalBeneficiaries?.length ?? 0) > 1;

      return (
        <Layout nav={<OBNavigation onPrev={onPrev} />} variant="larger">
          <Box paddingBottom="space-64">
            <Header title={locales.title} />

            <HStack gap="space-8" justifyContent="space-between">
              <VStack align="stretch" gap="space-4">
                <Text fontWeight="weight-500" size="md" variant="primary">
                  {legalName}
                </Text>

                <Text size="sm" variant="secondary">
                  {legalForm}
                </Text>
                <Text size="sm" variant="secondary">
                  {formatActivityType(activityType)}
                </Text>
              </VStack>

              {hasMultipleBeneficiaries ? null : (
                <Button
                  alignSelf="right"
                  iconPosition="right"
                  onClick={modalProps.onOpen}
                  variant="danger"
                >
                  {locales.reset}
                </Button>
              )}
            </HStack>

            <Divider marginY={40} />

            <VStack align="stretch" gap="space-4" marginBottom="space-32">
              <Text fontWeight="weight-500" size="md" variant="primary">
                {locales.company.title}
              </Text>
              <Text size="sm" variant="secondary">
                {locales.company.subtitle}
              </Text>
            </VStack>

            <VStack align="stretch" gap="space-24">
              <CompanyInformation
                company={{
                  city,
                  country,
                  legalName,
                  street,
                  streetAddition: streetAddition ?? '',
                  tradeName,
                  zip,
                }}
                {...(!hasMultipleBeneficiaries && {
                  onEdit: modalProps.onOpen,
                })}
              />

              <LegalInformation
                company={{
                  activityType,
                  legalRegistrationNumber,
                  registrationDate,
                }}
                {...(!hasMultipleBeneficiaries && {
                  onEdit: modalProps.onOpen,
                })}
              />
            </VStack>

            <Text
              fontWeight="weight-500"
              marginBottom="space-32"
              marginTop="space-40"
              size="md"
              variant="primary"
            >
              {locales.financialTitle}
            </Text>

            <FiscalInformation
              complianceValues={{
                incomeRange: incomeRange ?? '',
                isSpecifiedUSPerson: isSpecifiedUSPerson ?? undefined,
                personalAssets: personalAssets ?? '',
              }}
              {...(!hasMultipleBeneficiaries && { onEdit: modalProps.onOpen })}
              taxResidencies={taxResidencies}
            />
            <ResetOnboardingApplicationModal
              companyProfile={{
                activityType,
                legalForm,
                legalName,
                legalRegistrationNumber,
                registrationDate,
              }}
              isOpen={modalProps.isOpen}
              onAbort={onAbort}
              onClose={modalProps.onClose}
            />
          </Box>
        </Layout>
      );
    },
  );
